import firebase from "../../../components/Firebase.js";
import FindNextDay from "../../../functions/FindNextDay.js";
import YYYYMMDDConverter from "./YYYYMMDDConverter.js";
import CompareItemNames from "../../../functions/CompareItemNames.js";
import CommitIndividualOrder from "./CommitIndividualOrder.js";
import { sendOrderEmail } from "../OrderEmails.js";
import Swal from "sweetalert2";

function pickupDetails(selection) {
  if (!selection) throw new Error("Please select a pickup location.");
  const { communityLocationFarmList, ...communityPickup } = selection;
  return selection.pickupLocation
    ? { pickupLocation: selection.pickupLocation, communityPickup }
    : { pickupLocation: selection, communityPickup: [] };
}

function pickupDate(location, today) {
  const weekdays = {
    Mon: 1,
    Tues: 2,
    Wed: 3,
    Thurs: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };
  const cutoff = weekdays[location.orderCutoff];
  const pickup = weekdays[location.pickupDay];
  if (!cutoff || !pickup)
    throw new Error(
      "The pickup schedule is unavailable. Please refresh your basket.",
    );
  const date = YYYYMMDDConverter(
    FindNextDay(FindNextDay(today, cutoff), pickup),
  );
  return `${date.substring(4, 6)}-${date.substring(6, 8)}-${date.substring(
    0,
    4,
  )}`;
}

function logFailure(message, reference, error) {
  console.error(message, {
    orderId: reference.id,
    code: error.code ?? "unknown",
    message: error.message,
  });
}

async function recordConfirmation(reference, status, error) {
  try {
    await reference.update({ confirmationStatus: status });
  } catch (updateError) {
    logFailure(
      "Could not record order confirmation status",
      reference,
      updateError,
    );
  }
  if (error) logFailure("Saved order confirmation failed", reference, error);
}

async function confirmSavedOrder(reference, params) {
  try {
    await sendOrderEmail(params);
  } catch (error) {
    await recordConfirmation(reference, "failed", error);
    await Swal.fire({
      title: "Order placed; confirmation email unavailable",
      text: "Your order is saved in your account. Please do not place it again. Contact the marketplace if you need help.",
      icon: "warning",
    });
    return;
  }
  await recordConfirmation(reference, "sent");
}

/** Customer checkout owns persistence; email delivery never changes stock or credits. */
export default async function IndividualOrder({
  userInfo,
  foodItems,
  farmerInfo,
  contactMethods,
  donationSubsidy,
  customerContribution,
  basketOrder,
  deliveryFee = 0,
  cashPortion = 0,
  creditPortion = 0,
  paymentMethod = "cash",
  costTotal,
  grandTotal,
  effects,
}) {
  const database = firebase.firestore();
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Please sign in before placing your order.");
  const userReference = database.collection("Users").doc(user.uid);
  const now = YYYYMMDDConverter();
  const orderReference = userReference.collection("Orders").doc(now);
  effects.setPlacingOrder(true);
  let saved;
  try {
    if (foodItems.length === 0) throw new Error("Your basket is empty.");
    const pickup = pickupDetails(userInfo.pickupLocation);
    const foodList = [...foodItems].sort((a, b) =>
      CompareItemNames(a, b, "item"),
    );
    const orderData = {
      orderDate: now,
      foodList,
      farmerList: farmerInfo,
      contactMethods,
      ...pickup,
      donationSubsidy: Number(donationSubsidy),
      customerContribution,
      cashPortion,
      creditPortion,
      phoneNumber: userInfo.phoneNumber,
      paymentMethod,
      ...(deliveryFee === 0 ? { deliveryFee: 0 } : {}),
    };
    saved = await CommitIndividualOrder({
      database,
      userReference,
      orderReference,
      orderData,
      userInfo,
      groups: database
        .collection("DistributionLocations")
        .doc(pickup.pickupLocation.name)
        .collection("CommunityOrders"),
      distributionDate: pickupDate(pickup.pickupLocation, new Date()),
      fromBasket: basketOrder,
    });
    // This updates local account state only; the transaction already saved the profile.
    effects.handleUserInfoChange?.(saved.profile, false, false, false, true);
    effects.setFoodItems?.([]);
    effects.setSnackBarOpen?.(true);
    await confirmSavedOrder(orderReference, [
      pickup.pickupLocation.name,
      foodList,
      { ...userInfo, ...saved.profile },
      null,
      grandTotal,
      Number(donationSubsidy),
      customerContribution,
      null,
      null,
      deliveryFee,
      cashPortion,
      creditPortion,
      costTotal,
    ]);
    return saved.order;
  } catch (error) {
    if (!saved) {
      logFailure("Customer checkout failed", orderReference, error);
      throw error;
    }
    logFailure("Saved order completion failed", orderReference, error);
    await Swal.fire({
      title: "Order placed",
      text: "Your order is saved. Please refresh your account to view it, and do not place it again.",
      icon: "warning",
    });
    return saved.order;
  } finally {
    effects.setPlacingOrder(false);
  }
}
